import { useEffect, useState } from 'react';
import { useAddMaterialLengthsMutation } from '../../../redux/api/material.ts';
import { Button, Form, InputNumber, Modal, Select, message, Typography, Row, Col, Space } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Material } from '../../../interfaces/MaterialInventory.interface';
import BuildingTag from '../../BuildingTag';
import { Lineitem } from '../../../interfaces/Ordel.Interface';
import AttributeCostJobField from '../AttributeCostJobField';
import AssociatedJobField from '../AssociatedJobField';
import MaterialLocationsSummaryModal from '../../Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import OperatorPinModal from '../../Operators/OperatorPinModal';

// Status options for adding lengths: In Stock and Quality Hold only (no Awaiting Delivery, Scrapped)
const ADD_LENGTH_STATUS_OPTIONS = ['In Stock', 'Quality Hold'];

// Attribute Cost options when adding a length (Scenario 2)
const ADD_LENGTH_ATTRIBUTE_COST_OPTIONS = [
	{ label: 'Error Correction', value: 'error_correction' },
	{ label: 'Fastlane Job Related', value: 'fastlane_job_related' },
	{ label: 'Swiss Job Related', value: 'swiss_job_related' },
];

function isJobRelated(value: string | null | undefined): boolean {
	if (!value) return false;
	const lower = String(value).toLowerCase();
	return lower.includes('job_related') || lower.includes('job related');
}

function getProductionMethod(value: string | null | undefined): string | undefined {
	if (!value) return undefined;
	const lower = String(value).toLowerCase();
	if (lower.includes('swiss')) return 'Swiss';
	if (lower.includes('fastlane')) return 'Fastlane';
	return undefined;
}

function attributeCostForProductionMethod(method: string | null | undefined): string | undefined {
	if (!method || typeof method !== 'string') return undefined;
	const m = method.trim().toLowerCase();
	if (m === 'fastlane') return 'fastlane_job_related';
	if (m === 'swiss') return 'swiss_job_related';
	return undefined;
}

/** Bridges Form.Item (value=id, onChange) with AssociatedJobField (job object). Form controls id; parent keeps job object for display. */
function AssociatedJobFormControl({
	associatedJob,
	onAssociatedJobChange,
	productionMethodFilter,
	disabled,
}: {
	associatedJob: Lineitem | null;
	onAssociatedJobChange: (job: Lineitem | null) => void;
	productionMethodFilter?: string;
	disabled?: boolean;
}) {
	return (
		<AssociatedJobField
			value={associatedJob}
			onChange={(job) => {
				onAssociatedJobChange(job);
			}}
			productionMethodFilter={productionMethodFilter}
			disabled={disabled}
			placeholder="Select Job"
		/>
	);
}

const AddMaterialLengthsModal = ({
	isAddMaterialLengthsModalOpen,
	onClose,
	material,
	refetchData,
	building_id,
	building_name,
}: {
	isAddMaterialLengthsModalOpen: boolean;
	onClose: () => void;
	material: Material;
	refetchData: () => void;
	building_id?: string;
	building_name?: string;
}) => {
	const [form] = Form.useForm();
	const [addMaterialLengths, { isLoading }] = useAddMaterialLengthsMutation();
	// Scenario 3: Associated Job per row (display only; form stores associated_job_id for submit)
	const [associatedJobByIndex, setAssociatedJobByIndex] = useState<Record<number, Lineitem | null>>({});
	// Inline validation: show list-level error when submit with no lengths (no toast)
	const [listError, setListError] = useState<string | null>(null);
	/** After Submit on form fields, ask for operator PIN before saving. */
	const [operatorPinOpen, setOperatorPinOpen] = useState(false);
	// Scenario 7: Material Location Summary modal after submit (PTB-4699)
	const [showLocationSummary, setShowLocationSummary] = useState(false);
	const [locationSummaryData, setLocationSummaryData] = useState<{
		materialInfo: { type: string; width?: number; height?: number; shape?: string; outer_diameter?: number; inner_diameter?: number; wall_thickness?: number };
		lengths: Array<{ id: string; friendlyId: string; location: string; length: number; isScrapped?: boolean; overflowMessage?: string }>;
	}>({ materialInfo: { type: 'N/A' }, lengths: [] });

	// Watch material_lengths so we can show/hide Associated Job when Attribute Cost is Fastlane/Swiss
	const materialLengthsWatch = Form.useWatch('material_lengths', form) ?? [];

	// At least one material length row is required
	const hasAtLeastOneLength = (materialLengthsWatch?.length ?? 0) > 0;

	// Clear list-level inline error when user adds at least one row
	useEffect(() => {
		if (hasAtLeastOneLength) setListError(null);
	}, [hasAtLeastOneLength]);

	// Reset inline error / PIN step when modal opens
	useEffect(() => {
		if (isAddMaterialLengthsModalOpen) {
			setListError(null);
			setOperatorPinOpen(false);
		}
	}, [isAddMaterialLengthsModalOpen]);

	/** Step 1: validate length rows, then ask for operator PIN. */
	const handleOk = () => {
		setListError(null);
		form
			.validateFields()
			.then((values) => {
				const lengths = values?.material_lengths;
				if (!lengths || !Array.isArray(lengths) || lengths.length === 0) {
					setListError('At least one material length entry is required.');
					return;
				}
				setListError(null);
				setOperatorPinOpen(true);
			})
			.catch(() => {
				// Validation failed: Form shows inline errors per field; scrollToFirstError scrolls to first invalid field
			});
	};

	/** Step 2: PIN resolved — create lengths and show location summary. */
	const handleOperatorConfirm = async (userId: number, _pin: string) => {
		if (isLoading) return;
		const values = form.getFieldsValue(true);
		const materialId =
			typeof material?.id === 'string' ? material.id : String(material?.id ?? '');
		const requestData = {
			...values,
			operator_id: userId,
			material: materialId,
			...(building_id ? { building_id } : {}),
		};

		try {
			const response = await addMaterialLengths(requestData).unwrap();
			const createdLengths = response?.material_lengths ?? [];
			if (createdLengths.length === 0) {
				message.success('No material lengths to add.');
				setOperatorPinOpen(false);
				form.resetFields();
				setAssociatedJobByIndex({});
				refetchData();
				onClose();
				return;
			}
			message.success('Material length created successfully!');
			const summaryData = {
				materialInfo: {
					type: material?.type?.display_name ?? 'N/A',
					width: material?.width != null ? Number(material.width) : undefined,
					height: material?.height != null ? Number(material.height) : undefined,
					outer_diameter: material?.outer_diameter != null ? Number(material.outer_diameter) : undefined,
					inner_diameter: material?.inner_diameter != null ? Number(material.inner_diameter) : undefined,
					wall_thickness: material?.wall_thickness != null ? Number(material.wall_thickness) : undefined,
					shape: material?.shape?.display_name,
				},
				lengths: createdLengths.map((length: { id: string; friendly_id: string; inventory_location?: { display_name?: string }; material_shelf?: { display_name?: string }; current_length?: number; overflow_message?: string }) => ({
					id: length.id,
					friendlyId: length.friendly_id ?? '',
					location: length.inventory_location?.display_name ?? length.material_shelf?.display_name ?? 'Unknown',
					length: Number(length.current_length ?? 0),
					isScrapped: false,
					overflowMessage: length.overflow_message || undefined,
				})),
			};
			setOperatorPinOpen(false);
			setLocationSummaryData(summaryData);
			setShowLocationSummary(true);
		} catch (_error) {
			message.error('Failed to create material length');
		}
	};

	const handleSummaryComplete = () => {
		setShowLocationSummary(false);
		form.resetFields();
		setAssociatedJobByIndex({});
		refetchData();
		onClose();
	};

	const handleClose = () => {
		if (showLocationSummary) return;
		setListError(null);
		setOperatorPinOpen(false);
		onClose();
		form.resetFields();
		setAssociatedJobByIndex({});
	};

	return (
		<>
			<Modal
				open={isAddMaterialLengthsModalOpen}
				title={
					<Space>
						<span>Add Material Lengths</span>
						<BuildingTag name={building_name} />
					</Space>
				}
				width={1200}
				centered
				maskClosable={false}
				keyboard={false}
				onCancel={handleClose}
				footer={[
					<Button key="cancel" onClick={handleClose}>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						loading={isLoading}
						disabled={!hasAtLeastOneLength}
						onClick={handleOk}
					>
						Submit
					</Button>,
				]}
			>
				<Form
					form={form}
					layout="vertical"
					name="material_length_form"
					requiredMark={false}
					scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
					initialValues={{
						material_lengths: [
							{
								current_length: 144,
								quantity: 1,
								status: 'In Stock',
							},
						],
					}}
				>
					{listError && (
						<div style={{ marginBottom: 12 }}>
							<Typography.Text type="danger">{listError}</Typography.Text>
						</div>
					)}
					<Form.List name="material_lengths">
						{(fields, { add, remove }) => (
							<>
								{fields.map(({ key, name, ...restField }) => (
									<div
										key={key}
										style={{
											marginBottom: 16,
											padding: '12px 0',
											borderBottom: '1px solid #f0f0f0',
											width: '100%',
											maxWidth: '100%',
											boxSizing: 'border-box',
										}}
									>
										<Row gutter={[16, 0]} align="top" wrap={false} style={{ width: '100%' }}>
											<Col flex="1" style={{ minWidth: 0 }}>
												<Form.Item
													{...restField}
													label={
														<>
															Current Length <Typography.Text type="danger">*</Typography.Text>
														</>
													}
													name={[name, 'current_length']}
													rules={[
														{ required: true, message: 'Current length is required' },
														{ type: 'number', max: 240, message: 'Must be a valid number' },
													]}
												>
													<InputNumber
														min={0.25}
														max={240}
														step={0.25}
														precision={2}
														placeholder="Enter current length"
														style={{ width: '100%' }}
														suffix="in"
													/>
												</Form.Item>
											</Col>
											<Col flex="1" style={{ minWidth: 0 }}>
												<Form.Item
													{...restField}
													label={
														<>
															Quantity <Typography.Text type="danger">*</Typography.Text>
														</>
													}
													name={[name, 'quantity']}
													rules={[{ required: true, message: 'Quantity is required' }]}
												>
													<InputNumber min={1} max={99} step={1} placeholder="Qty" style={{ width: '100%' }} />
												</Form.Item>
											</Col>
											<Col flex="1" style={{ minWidth: 0 }}>
												<Form.Item
													{...restField}
													label={
														<>
															Status <Typography.Text type="danger">*</Typography.Text>
														</>
													}
													name={[name, 'status']}
													rules={[{ required: true, message: 'Status is required' }]}
													initialValue="In Stock"
												>
													<Select
														showSearch
														optionFilterProp="label"
														options={ADD_LENGTH_STATUS_OPTIONS.map((x) => ({ label: x, value: x }))}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
											<Col flex="2" style={{ minWidth: 0 }}>
													<Form.Item
														name={[name, 'attribute_cost']}
														rules={[{ required: true, message: 'Attribute cost is required' }]}
														style={{ marginBottom: 24 }}
													>
														<AttributeCostJobField
															options={ADD_LENGTH_ATTRIBUTE_COST_OPTIONS}
															onAttributeCostChange={(val) => {
																// Form.Item injects onChange; we also clear associated job when switching to non-job-related
																if (!isJobRelated(val)) {
																	form.setFieldValue(['material_lengths', name, 'associated_job_id'], undefined);
																	setAssociatedJobByIndex((prev) => ({ ...prev, [name]: null }));
																}
															}}
															associatedJob={associatedJobByIndex[name] ?? null}
															onAssociatedJobChange={(job) => {
																form.setFieldValue(['material_lengths', name, 'associated_job_id'], job?.id ?? undefined);
																setAssociatedJobByIndex((prev) => ({ ...prev, [name]: job }));
																const currentCost = form.getFieldValue(['material_lengths', name, 'attribute_cost']);
																if (job?.partsbadger_production_method && isJobRelated(currentCost)) {
																	const matchingCost = attributeCostForProductionMethod(job.partsbadger_production_method);
																	if (matchingCost && currentCost !== matchingCost) {
																		form.setFieldValue(['material_lengths', name, 'attribute_cost'], matchingCost);
																	}
																}
															}}
															costRequired
															jobRequired={isJobRelated(materialLengthsWatch[name]?.attribute_cost)}
															jobColumn={
																isJobRelated(materialLengthsWatch[name]?.attribute_cost) ? (
																	<Form.Item
																		name={[name, 'associated_job_id']}
																		dependencies={[[name, 'attribute_cost']]}
																		style={{ marginBottom: 0 }}
																		rules={[
																			{
																				validator(_, value) {
																					const attributeCost = form.getFieldValue(['material_lengths', name, 'attribute_cost']);
																					if (isJobRelated(attributeCost) && !value) {
																						return Promise.reject(new Error('Associated job is required'));
																					}
																					return Promise.resolve();
																				},
																			},
																		]}
																	>
																		<AssociatedJobFormControl
																			associatedJob={associatedJobByIndex[name] ?? null}
																			onAssociatedJobChange={(job) => {
																				form.setFieldValue(['material_lengths', name, 'associated_job_id'], job?.id ?? undefined);
																				setAssociatedJobByIndex((prev) => ({ ...prev, [name]: job }));
																				const currentCost = materialLengthsWatch[name]?.attribute_cost;
																				if (job?.partsbadger_production_method && isJobRelated(currentCost)) {
																					const matchingCost = attributeCostForProductionMethod(job.partsbadger_production_method);
																					if (matchingCost && currentCost !== matchingCost) {
																						form.setFieldValue(['material_lengths', name, 'attribute_cost'], matchingCost);
																					}
																				}
																			}}
																			productionMethodFilter={getProductionMethod(materialLengthsWatch[name]?.attribute_cost)}
																		/>
																	</Form.Item>
																) : undefined
															}
														/>
													</Form.Item>
											</Col>
											<Col flex="0 0 32px" style={{ marginBottom: 24 }}>
												<CloseOutlined onClick={() => remove(name)} style={{ fontSize: 14, color: '#999' }} />
											</Col>
										</Row>
									</div>
								))}
								<Form.Item>
									<Button
										type="dashed"
										onClick={() => add({ status: 'In Stock' })}
										block
										icon={<PlusOutlined />}
									>
										Add Material Length
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>
				</Form>
				{showLocationSummary && (
					<MaterialLocationsSummaryModal
						visible={showLocationSummary}
						onComplete={handleSummaryComplete}
						materialInfo={locationSummaryData.materialInfo}
						lengths={locationSummaryData.lengths}
						title="Material Locations Summary"
						source="receiving"
					/>
				)}
			</Modal>

			<OperatorPinModal
				open={isAddMaterialLengthsModalOpen && operatorPinOpen && !showLocationSummary}
				title="Identify operator"
				description="Enter the operator PIN to confirm adding these material lengths."
				label="Operator"
				expectedGroups={['Machine Operators']}
				onCancel={() => setOperatorPinOpen(false)}
				onConfirm={handleOperatorConfirm}
			/>
		</>
	);
};

export default AddMaterialLengthsModal;
